/**
 * CCC Coffee Collection — client-side filters, journey cards, section visibility.
 * Used on /collections/roasted-coffee-beans (main-coffee-collection section).
 */
import { getScrollContainer, getScrollTop, scrollTo } from '@theme/scroll-container';

  const SORT_ALIASES = {
    'price-asc': 'price-ascending',
    'price-desc': 'price-descending',
    'title-asc': 'title-ascending',
  };

  const DEFAULT_SORT = 'best-selling';
  const DEFAULT_AVAILABILITY = 'in-stock';
  const BEST_SELLING_CACHE_PREFIX = 'ccc-coffee-best-selling:';
  const BEST_SELLING_CACHE_TTL_MS = 30 * 60 * 1000;

  function normalizeSortValue(value) {
    if (!value) return DEFAULT_SORT;
    return SORT_ALIASES[value] || value;
  }

  function restoreScrollPosition(scrollX, scrollY) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollTo({ left: scrollX, top: scrollY, behavior: 'auto' });
      });
    });
  }

  function shouldResetScrollOnInit() {
    const nav = performance.getEntriesByType('navigation')[0];
    return !nav || nav.type !== 'back_forward';
  }

  function settleInitialPageScroll() {
    if (!shouldResetScrollOnInit()) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollTo({ top: 0, left: 0, behavior: 'auto' });

        try {
          if (typeof history.state !== 'object' || history.state === null) return;
          if (!('scrollTop' in history.state)) return;
          const nextState = { ...history.state };
          delete nextState.scrollTop;
          history.replaceState(nextState, '');
        } catch (_) {
          // replaceState can throw if state exceeds browser limits
        }
      });
    });
  }

  window.addEventListener('pageshow', () => {
    if (!document.querySelector('[data-coffee-collection]')) return;
    settleInitialPageScroll();
  });

  /**
   * @param {HTMLElement} root
   */
  function initCoffeeCollection(root) {
    if (!root || root.dataset.coffeeCollectionInit === 'true') return;
    root.dataset.coffeeCollectionInit = 'true';

    const cards = Array.from(root.querySelectorAll('.coffee-collection__item'));
    const sections = Array.from(root.querySelectorAll('[data-coffee-section]'));
    const filterRoots = Array.from(root.querySelectorAll('[data-coffee-filters]'));
    const emptyEl = root.querySelector('[data-coffee-empty]');
    const sortSelect = root.querySelector('[data-coffee-sort]');

    /** @type {Record<string, string[]>} */
    const state = {
      subscription: [],
      browse: [],
      availability: [DEFAULT_AVAILABILITY],
      sale: [],
      origin: [],
      process: [],
      flavour: [],
      descriptor: [],
      strength: [],
      roastStrength: [],
    };

    let searchQuery = '';
    /** @type {number | undefined} */
    let searchDebounceId;

    const ROAST_STRENGTH_MAX = 6;
    const ROAST_STRENGTH_LABELS = {
      '1': 'Very light',
      '2': 'Light',
      '3': 'Medium light',
      '4': 'Medium',
      '5': 'Dark',
      '6': 'Very dark',
    };

    /** @type {{ slug: string, label: string, test: (name: string) => boolean }[]} */
    const PROCESS_GROUP_RULES = [
      { slug: 'decaf', label: 'Decaf', test: (name) => /decaf/i.test(name) },
      { slug: 'honey', label: 'Honey', test: (name) => /honey/i.test(name) },
      { slug: 'naturals', label: 'Naturals', test: (name) => /natural/i.test(name) },
      { slug: 'washed', label: 'Washed', test: (name) => /washed/i.test(name) },
    ];

    const PROCESS_FILTER_ORDER = ['decaf', 'honey', 'naturals', 'washed'];

    /** High-level browse keys shared by journey cards and sidebar Browse Coffee. */
    const BROWSE_KEYS = new Set(['espresso', 'filter', 'special', 'decaf', 'blends', 'singles']);

    function parseList(value) {
      if (!value) return [];
      return value.split('|').map((v) => v.trim()).filter(Boolean);
    }


    function neutraliseResultsHashOnLoad() {
      if (window.location.hash !== '#CoffeeCollectionResults') return;
      history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
      scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }

    function getCardExtraction(card) {
      const raw = (card.dataset.extraction || '').trim().toLowerCase();
      return raw || 'espresso';
    }

    function cardMatchesSearch(card) {
      const query = searchQuery.trim().toLowerCase();
      if (!query) return true;

      const title = (card.dataset.productTitle || '').toLowerCase();
      return title.includes(query);
    }

    function cardMatchesBrowse(card) {
      const browse = state.browse[0];
      if (!browse || browse === 'all') return true;

      const extraction = getCardExtraction(card);
      const coffeeType = (card.dataset.coffeeType || 'single').toLowerCase();
      const section = card.dataset.merchandiseSection || '';
      const isDecaf = card.dataset.decaf === 'true';

      switch (browse) {
        case 'espresso':
          return extraction === 'espresso';
        case 'filter':
          return extraction === 'filter';
        case 'special':
          return section === 'special';
        case 'decaf':
          return isDecaf;
        case 'blends':
          return coffeeType === 'blend';
        case 'singles':
          return coffeeType !== 'blend';
        default:
          return true;
      }
    }

    function cardMatchesSubscription(card) {
      const selected = state.subscription[0];
      if (!selected || selected === 'all') return true;
      if (selected === 'subscribe') return card.dataset.subscription === 'true';
      return true;
    }

    function cardMatchesMulti(card, key, datasetKey) {
      const selected = state[key];
      if (!selected.length) return true;
      const values = parseList(card.dataset[datasetKey] || '');
      return selected.some((sel) => values.includes(sel));
    }

    /**
     * @param {string} slug
     * @param {string} label
     * @returns {{ slug: string, label: string } | null}
     */
    function resolveProcessFilterEntry(slug, label) {
      const name = String(label || slug).trim();
      if (!name) return null;

      for (const rule of PROCESS_GROUP_RULES) {
        if (rule.test(name)) {
          return { slug: rule.slug, label: rule.label };
        }
      }

      const resolvedSlug = slug || toSlug(name);
      if (!resolvedSlug || name === 'MetaobjectDrop' || resolvedSlug === 'metaobjectdrop') return null;
      return { slug: resolvedSlug, label: name };
    }

    /** @param {HTMLElement} card */
    function getCardProcessFilterSlugs(card) {
      /** @type {Set<string>} */
      const slugs = new Set();
      parseEntries(card.dataset.processEntries).forEach(({ slug, label }) => {
        const resolved = resolveProcessFilterEntry(slug, label);
        if (resolved) slugs.add(resolved.slug);
      });
      return slugs;
    }

    function cardMatchesProcess(card) {
      const selected = state.process;
      if (!selected.length) return true;
      const cardSlugs = getCardProcessFilterSlugs(card);
      return selected.some((sel) => cardSlugs.has(sel));
    }

    function cardMatchesAvailability(card) {
      const selected = state.availability[0];
      if (!selected) return true;
      if (selected === 'in-stock') return card.dataset.available === 'true';
      if (selected === 'out-of-stock') return card.dataset.available === 'false';
      return true;
    }

    /**
     * Wheel tokens: slugs joined by '~' within a token are alternatives (OR,
     * same flavour family); separate tokens must all match (AND).
     */
    function cardMatchesDescriptor(card) {
      const selected = state.descriptor;
      if (!selected.length) return true;
      const values = parseList(card.dataset.descriptor || '');
      return selected.every((token) => token.split('~').some((slug) => values.includes(slug)));
    }

    function cardMatchesSale(card) {
      if (!state.sale.length) return true;
      return card.dataset.onSale === 'true';
    }

    function cardMatchesRoastStrength(card) {
      const selected = state.roastStrength;
      if (!selected.length) return true;
      const rating = card.dataset.roastStrength;
      if (!rating) return false;
      return selected.includes(rating);
    }

    function cardIsVisible(card) {
      return (
        cardMatchesSubscription(card) &&
        cardMatchesBrowse(card) &&
        cardMatchesSearch(card) &&
        cardMatchesAvailability(card) &&
        cardMatchesSale(card) &&
        cardMatchesRoastStrength(card) &&
        cardMatchesMulti(card, 'origin', 'origin') &&
        cardMatchesProcess(card) &&
        cardMatchesMulti(card, 'flavour', 'flavour') &&
        cardMatchesDescriptor(card) &&
        cardMatchesMulti(card, 'strength', 'strength')
      );
    }

    /** @type {Map<string, number>|null} */
    let bestSellingRanks = null;

    /** @type {ReturnType<typeof collectFacetMaps> | null} */
    let facetMaps = null;

    let drawerDynamicFiltersReady = false;

    /** @type {Map<Element, HTMLElement[]>} */
    const initialGridOrders = new Map();

    function captureInitialGridOrders() {
      sections.forEach((section) => {
        const grid = section.querySelector('.coffee-collection__grid');
        if (!grid) return;
        initialGridOrders.set(grid, Array.from(grid.querySelectorAll('.coffee-collection__item')));
      });
    }

    function readBestSellingCache(pathname) {
      try {
        const raw = sessionStorage.getItem(`${BEST_SELLING_CACHE_PREFIX}${pathname}`);
        if (!raw) return null;

        const parsed = JSON.parse(raw);
        if (!parsed?.cachedAt || !Array.isArray(parsed.ranks)) return null;
        if (Date.now() - parsed.cachedAt > BEST_SELLING_CACHE_TTL_MS) return null;

        return new Map(parsed.ranks);
      } catch {
        return null;
      }
    }

    function writeBestSellingCache(pathname, ranks) {
      try {
        sessionStorage.setItem(
          `${BEST_SELLING_CACHE_PREFIX}${pathname}`,
          JSON.stringify({
            cachedAt: Date.now(),
            ranks: Array.from(ranks.entries()),
          })
        );
      } catch {
        // sessionStorage may be unavailable or full
      }
    }

    async function loadBestSellingRanks() {
      const pathname = window.location.pathname;
      const cached = readBestSellingCache(pathname);
      if (cached) {
        bestSellingRanks = cached;
        return;
      }

      try {
        const url = new URL(pathname, window.location.origin);
        url.searchParams.set('sort_by', 'best-selling');
        const response = await fetch(url.toString(), { credentials: 'same-origin' });
        if (!response.ok) return;

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const ranks = new Map();
        let index = 0;

        doc.querySelectorAll('.coffee-collection__item[data-product-id]').forEach((item) => {
          const id = item.getAttribute('data-product-id');
          if (!id || ranks.has(id)) return;
          ranks.set(id, index);
          index += 1;
        });

        bestSellingRanks = ranks;
        writeBestSellingCache(pathname, ranks);
      } catch {
        bestSellingRanks = new Map();
      }
    }

    function hydrateBestSellingRanksFromCache() {
      const cached = readBestSellingCache(window.location.pathname);
      if (!cached) return false;
      bestSellingRanks = cached;
      return true;
    }

    /**
     * @param {{ eager?: boolean }} [options]
     */
    async function ensureBestSellingRanks({ eager = false } = {}) {
      if (hydrateBestSellingRanksFromCache()) return;

      if (eager) {
        await loadBestSellingRanks();
      }
    }

    function compareCards(a, b, sortBy) {
      const productIdA = a.dataset.productId || '';
      const productIdB = b.dataset.productId || '';

      switch (sortBy) {
        case 'title-ascending':
          return (a.dataset.productTitle || '').toLowerCase().localeCompare((b.dataset.productTitle || '').toLowerCase());
        case 'title-descending':
          return (b.dataset.productTitle || '').toLowerCase().localeCompare((a.dataset.productTitle || '').toLowerCase());
        case 'price-ascending': {
          const priceA = parseInt(a.dataset.variantPrice || '0', 10);
          const priceB = parseInt(b.dataset.variantPrice || '0', 10);
          return priceA - priceB;
        }
        case 'price-descending': {
          const priceA = parseInt(a.dataset.variantPrice || '0', 10);
          const priceB = parseInt(b.dataset.variantPrice || '0', 10);
          return priceB - priceA;
        }
        case 'created-ascending': {
          const createdA = parseInt(a.dataset.createdAt || '0', 10);
          const createdB = parseInt(b.dataset.createdAt || '0', 10);
          return createdA - createdB;
        }
        case 'created-descending': {
          const createdA = parseInt(a.dataset.createdAt || '0', 10);
          const createdB = parseInt(b.dataset.createdAt || '0', 10);
          return createdB - createdA;
        }
        case 'best-selling': {
          const rankA = bestSellingRanks?.get(productIdA) ?? parseInt(a.dataset.sortIndex || '9999', 10);
          const rankB = bestSellingRanks?.get(productIdB) ?? parseInt(b.dataset.sortIndex || '9999', 10);
          return rankA - rankB;
        }
        default:
          return 0;
      }
    }

    function sortSectionGrids(options = {}) {
      const { preserveScroll = false } = options;
      const scrollContainer = getScrollContainer();
      const scrollX = preserveScroll ? scrollContainer.scrollLeft : 0;
      const scrollY = preserveScroll ? getScrollTop() : 0;
      const sortBy = normalizeSortValue(sortSelect?.value || DEFAULT_SORT);

      sections.forEach((section) => {
        const grid = section.querySelector('.coffee-collection__grid');
        if (!grid) return;

        const items = Array.from(grid.querySelectorAll('.coffee-collection__item'));

        if (sortBy === 'manual') {
          const initial = initialGridOrders.get(grid);
          if (initial) {
            initial.forEach((item) => grid.appendChild(item));
          }
          return;
        }

        items.sort((a, b) => compareCards(a, b, sortBy));
        items.forEach((item) => grid.appendChild(item));
      });

      if (preserveScroll) {
        restoreScrollPosition(scrollX, scrollY);
      }
    }

    function getUniqueCardsByProduct(cardList) {
      /** @type {Map<string, HTMLElement>} */
      const byProduct = new Map();
      cardList.forEach((card) => {
        const productId = card.dataset.productId;
        if (!productId || byProduct.has(productId)) return;
        byProduct.set(productId, card);
      });
      return Array.from(byProduct.values());
    }

    function getFacetCountTestState(group, value, input) {
      /** @type {Record<string, string[]>} */
      const testState = {};
      Object.keys(state).forEach((key) => {
        testState[key] = [];
      });
      // Counts must respect the availability baseline (default: in stock),
      // or options can advertise products the grid will never show.
      testState.availability = state.availability.slice();

      if (input.type === 'radio') {
        if (value === 'all' && (group === 'browse' || group === 'subscription')) {
          testState[group] = [];
        } else {
          testState[group] = [value];
        }
      } else if (input.type === 'checkbox') {
        testState[group] = [value];
      }

      return testState;
    }

    function isDrawerFilterInput(input) {
      return !!input.closest('#coffee-filters-drawer');
    }

    function isFilterDrawerOpen() {
      const drawer = document.getElementById('coffee-filters-drawer');
      return drawer instanceof HTMLElement && drawer.isOpen === true;
    }

    function isSidebarHiddenForLayout() {
      return window.matchMedia('(max-width: 1199px)').matches;
    }

    /**
     * @param {Record<string, string[]>} testState
     * @param {HTMLElement[]} uniqueCards
     */
    function countFacetVisibleProducts(testState, uniqueCards) {
      const saved = {};
      for (const key of Object.keys(state)) {
        saved[key] = state[key];
        state[key] = testState[key] ?? [];
      }

      const visibleProductIds = new Set();
      for (const card of uniqueCards) {
        const id = card.dataset.productId;
        if (!id || visibleProductIds.has(id)) continue;
        if (cardIsVisible(card)) visibleProductIds.add(id);
      }

      for (const key of Object.keys(saved)) {
        state[key] = saved[key];
      }

      return visibleProductIds.size;
    }

    function updateEmptyFilterGroups() {
      root.querySelectorAll('.coffee-filters__details').forEach((details) => {
        const items = details.querySelectorAll(':scope > ul > li');
        const anyVisible = Array.from(items).some((item) => !item.hidden);
        details.hidden = items.length > 0 && !anyVisible;
      });

      root.querySelectorAll('.coffee-filters__group').forEach((group) => {
        if (group.classList.contains('coffee-filters__group--subscription')) return;

        const beanItems = group.querySelectorAll('.coffee-filters__bean-scale-item');
        if (beanItems.length) {
          const anyVisible = Array.from(beanItems).some((item) => !item.hidden);
          group.hidden = !anyVisible;
          return;
        }

        const browseItems = group.querySelectorAll('.coffee-filters__list--browse > .coffee-filters__option');
        if (browseItems.length) {
          const anyVisible = Array.from(browseItems).some((item) => !item.hidden);
          group.hidden = !anyVisible;
        }
      });

      root.querySelectorAll('.coffee-filters__group--sale').forEach((group) => {
        const items = group.querySelectorAll('.coffee-filters__option');
        const anyVisible = Array.from(items).some((item) => !item.hidden);
        group.hidden = !anyVisible;
      });
    }

    function updateFilterCounts(options = {}) {
      const { includeHidden = false } = options;
      if (!filterRoots.length) return;

      const uniqueCards = getUniqueCardsByProduct(cards);
      const drawerOpen = isFilterDrawerOpen();
      /** @type {Map<string, number>} */
      const countCache = new Map();

      for (const input of root.querySelectorAll('[data-coffee-filter-input]')) {
        if (!(input instanceof HTMLInputElement)) continue;
        // The two visibility skips below keep interaction-time counting cheap. On the
        // one-time load pass (includeHidden) we bypass them so the static-group count
        // spans (browse/sale/availability/subscription) get filled in the closed drawer
        // and the sub-1200px hidden sidebar too, instead of showing their hardcoded 0.
        if (!includeHidden) {
          if (isDrawerFilterInput(input) && !drawerOpen) continue;
          if (input.closest('.coffee-collection__sidebar') && isSidebarHiddenForLayout()) continue;
        }

        const group = input.dataset.filterGroup;
        const value = input.dataset.filterValue;
        if (!group || !value) continue;

        const cacheKey = `${group}:${value}:${input.type}`;
        let count = countCache.get(cacheKey);
        if (count === undefined) {
          const testState = getFacetCountTestState(group, value, input);
          count = countFacetVisibleProducts(testState, uniqueCards);
          countCache.set(cacheKey, count);
        }

        const countSpan = input.closest('label')?.querySelector('[data-filter-count]');
        if (countSpan) countSpan.textContent = String(count);

        const option =
          input.closest('.coffee-filters__option') ||
          input.closest('.coffee-filters__bean-scale-item') ||
          input.closest('li');
        const isBrowse = group === 'browse';
        const isUniversalOption = value === 'all' && (group === 'browse' || group === 'subscription');
        const shouldHide = count === 0 && !input.checked && !isUniversalOption;

        if (option instanceof HTMLElement) {
          option.hidden = shouldHide;
        }

        input.disabled = isBrowse ? false : shouldHide;
      }

      root.querySelectorAll('[data-coffee-journey]').forEach((btn) => {
        const value = btn.dataset.coffeeJourney;
        if (!value) return;

        const cacheKey = `browse:${value}:radio`;
        let count = countCache.get(cacheKey);
        if (count === undefined) {
          const testState = getFacetCountTestState('browse', value, { type: 'radio' });
          count = countFacetVisibleProducts(testState, uniqueCards);
          countCache.set(cacheKey, count);
        }

        const active = state.browse[0] === value;
        btn.hidden = count === 0 && !active;
      });

      const journeyPanel = root.querySelector('.coffee-journey');
      if (journeyPanel) {
        const anyVisible = Array.from(root.querySelectorAll('[data-coffee-journey]')).some((btn) => !btn.hidden);
        journeyPanel.hidden = !anyVisible;
      }

      updateEmptyFilterGroups();
    }

    function syncInputsFromState() {
      root.querySelectorAll('[data-coffee-filter-input]').forEach((input) => {
        const group = input.dataset.filterGroup;
        const value = input.dataset.filterValue;
        if (!group) return;

        if (input.type === 'radio') {
          if (group === 'browse' || group === 'subscription') {
            const active = state[group][0] || 'all';
            input.checked = active === value || (active === '' && value === 'all');
          } else if (group === 'availability') {
            const active = state.availability[0] || DEFAULT_AVAILABILITY;
            input.checked = active === value;
          } else if (group === 'roastStrength' && input.closest('[data-coffee-roast-strength-sidebar]')) {
            input.checked = state.roastStrength[0] === value;
          } else {
            input.checked = state[group][0] === value;
          }
        } else if (input.type === 'checkbox') {
          input.checked = state[group].includes(value);
        }

        const label = input.closest('label');
        if (!label) return;

        const isBeanScale = label.classList.contains('coffee-filters__bean-scale-option');
        const isActive =
          isBeanScale && group === 'roastStrength'
            ? state.roastStrength[0] === value
            : input.checked;
        label.classList.toggle('coffee-filters__label--active', label.classList.contains('coffee-filters__label') && isActive);
        label.classList.toggle('coffee-filters__pill--active', label.classList.contains('coffee-filters__pill') && isActive);
        label.classList.toggle('coffee-collection__price-option--active', label.classList.contains('coffee-collection__price-option') && isActive);
        label.classList.toggle(
          'coffee-collection__roast-strength-pill--active',
          label.classList.contains('coffee-collection__roast-strength-pill') && isActive
        );
        label.classList.toggle(
          'coffee-filters__bean-scale-option--active',
          label.classList.contains('coffee-filters__bean-scale-option') && isActive
        );
        label.classList.toggle(
          'coffee-collection__subscription-option--active',
          label.classList.contains('coffee-collection__subscription-option') && isActive
        );

        if (label.classList.contains('coffee-filters__label--browse')) {
          if (isActive) {
            label.setAttribute('aria-current', 'page');
          } else {
            label.removeAttribute('aria-current');
          }
        }
      });

      root.querySelectorAll('[data-coffee-journey]').forEach((btn) => {
        const journey = btn.dataset.coffeeJourney;
        const browseActive = state.browse[0] || '';
        const active = browseActive === journey;
        btn.classList.toggle('coffee-journey__card--active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      root.querySelectorAll('[data-coffee-roast-strength-sidebar]').forEach((scale) => {
        const selected = state.roastStrength[0] || '';
        if (selected) {
          scale.dataset.selected = selected;
        } else {
          scale.removeAttribute('data-selected');
        }
      });
    }

    function normalizeBrowseState() {
      const current = state.browse[0];
      if (!current || current === 'all' || !BROWSE_KEYS.has(current)) {
        state.browse = [];
      } else {
        state.browse = [current];
      }
    }

    function syncSearchInput() {
      root.querySelectorAll('[data-coffee-search]').forEach((input) => {
        if (input instanceof HTMLInputElement && input.value !== searchQuery) {
          input.value = searchQuery;
        }
      });
    }

    function applyDefaultAvailability() {
      const params = new URLSearchParams(window.location.search);
      if (!params.has('availability') || !state.availability.length) {
        state.availability = [DEFAULT_AVAILABILITY];
      }
    }

    function readStateFromUrl() {
      const params = new URLSearchParams(window.location.search);
      searchQuery = params.get('search') || '';
      syncSearchInput();
      Object.keys(state).forEach((key) => {
        const raw = params.get(key);
        state[key] = raw ? raw.split(',').filter(Boolean) : [];
      });
      params.delete('extraction');
      normalizeBrowseState();
      const legacyFormat = params.get('coffeeFormat');
      if (legacyFormat && !state.browse.length) {
        const formatValue = legacyFormat.split(',')[0];
        if (formatValue === 'blends' || formatValue === 'singles') {
          state.browse = [formatValue];
        }
        params.delete('coffeeFormat');
      }
      if (params.get('sort')) {
        if (sortSelect) sortSelect.value = normalizeSortValue(params.get('sort'));
      } else if (sortSelect) {
        sortSelect.value = DEFAULT_SORT;
      }
      applyDefaultAvailability();
    }

    function writeStateToUrl() {
      const params = new URLSearchParams(window.location.search);
      Object.entries(state).forEach(([key, values]) => {
        if (key === 'availability' && values[0] === DEFAULT_AVAILABILITY) {
          params.delete(key);
          return;
        }
        if (values.length) params.set(key, values.join(','));
        else params.delete(key);
      });
      params.delete('coffeeFormat');
      params.delete('extraction');
      const trimmedSearch = searchQuery.trim();
      if (trimmedSearch) params.set('search', trimmedSearch);
      else params.delete('search');
      if (sortSelect && sortSelect.value && normalizeSortValue(sortSelect.value) !== DEFAULT_SORT) {
        params.set('sort', normalizeSortValue(sortSelect.value));
      } else {
        params.delete('sort');
      }
      const query = params.toString();
      const next = query ? `${window.location.pathname}?${query}` : window.location.pathname;
      window.history.replaceState({}, '', next);
    }

    /** @returns {number} */
    function getHeaderScrollOffset() {
      // The theme sets --header-height/--header-group-height on <body>, not <html>.
      const bodyStyle = getComputedStyle(document.body);
      const groupHeight = parseFloat(bodyStyle.getPropertyValue('--header-group-height')) || 0;
      const headerHeight = parseFloat(bodyStyle.getPropertyValue('--header-height')) || 0;
      const measured = getStickyObstructionBottom();
      return Math.round(Math.max(groupHeight, headerHeight, measured)) + 12;
    }

    /**
     * Live-measures anything sticky/fixed overlapping the top of the viewport
     * (sticky headers, announcement bars) as a fallback when the CSS variables
     * are stale or unset.
     * @returns {number}
     */
    function getStickyObstructionBottom() {
      let bottom = 0;
      for (const el of document.elementsFromPoint(window.innerWidth / 2, 1)) {
        if (el === document.body || el === document.documentElement) continue;
        const position = getComputedStyle(el).position;
        if (position === 'sticky' || position === 'fixed') {
          bottom = Math.max(bottom, el.getBoundingClientRect().bottom);
        }
      }
      return bottom;
    }

    function getResultsScrollTarget() {
      return root.querySelector('[data-coffee-scroll-target]') || document.getElementById('CoffeeCollectionResults');
    }

    /** @type {number} */
    let scrollToResultsRaf = 0;

    function scheduleScrollToResults() {
      if (scrollToResultsRaf) cancelAnimationFrame(scrollToResultsRaf);
      scrollToResultsRaf = requestAnimationFrame(() => {
        scrollToResultsRaf = requestAnimationFrame(() => {
          scrollToResultsRaf = 0;
          scrollToResultsIfNeeded();
        });
      });
    }

    function scrollToResultsIfNeeded() {
      const target = getResultsScrollTarget();
      if (!(target instanceof HTMLElement)) return;

      const headerOffset = getHeaderScrollOffset();
      const rect = target.getBoundingClientRect();
      const scrollTop = getScrollTop();
      const targetDocTop = rect.top + scrollTop;
      const alreadyVisible = rect.top >= headerOffset - 8 && rect.top <= window.innerHeight * 0.55;

      if (alreadyVisible) return;

      const belowResults = scrollTop > targetDocTop - headerOffset - 24;
      const aboveViewport = rect.bottom < headerOffset + 16;
      const belowViewport = rect.top > window.innerHeight - 80;

      if (!belowResults && !aboveViewport && !belowViewport) return;

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      scrollTo({
        top: Math.max(0, targetDocTop - headerOffset),
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
    }

    /**
     * @param {EventTarget|null} input
     * @param {{ closeDrawerIfOpen?: boolean }} [options]
     */
    function afterFilterInteraction(input, { closeDrawerIfOpen = false } = {}) {
      const drawerEl = document.getElementById('coffee-filters-drawer');
      const isMobile = window.matchMedia('(max-width: 1199px)').matches;
      const drawerOpen = isMobile && drawerEl instanceof HTMLElement && drawerEl.isOpen === true;

      if (drawerOpen) {
        // A facet tap with the drawer open keeps it open so shoppers can stack
        // filters; the results sit behind the drawer, so we don't scroll. The
        // drawer only closes via an explicit action (the "Show N coffees"
        // button or Clear all), which passes closeDrawerIfOpen.
        if (closeDrawerIfOpen && typeof drawerEl.close === 'function') {
          drawerEl.close().then(() => scheduleScrollToResults());
        }
        return;
      }

      scheduleScrollToResults();
    }

    function setCountNode(el, value) {
      if (!el) return;
      const str = String(value);
      if (el.firstChild && el.firstChild.nodeType === 3) {
        // characterData update only — no childList mutation, so the theme's
        // MutationObserver autoloader doesn't rescan the DOM (perf trap).
        if (el.firstChild.data !== str) el.firstChild.data = str;
      } else {
        el.textContent = str;
      }
    }

    let lastResultsEmpty = null;
    function updateResultsCount(total) {
      root.querySelectorAll('[data-coffee-visible-count]').forEach((el) => {
        const many = el.dataset.labelCount;
        if (many) {
          const tmpl = total === 1 && el.dataset.labelCountOne ? el.dataset.labelCountOne : many;
          setCountNode(el, tmpl.replace('{count}', String(total)));
        } else {
          setCountNode(el, total);
        }
      });

      const btn = root.querySelector('[data-coffee-show-results]');
      if (!btn) return;
      const labelEl = btn.querySelector('[data-coffee-show-results-label]');
      let label;
      if (total === 0) label = btn.dataset.labelEmpty || '';
      else if (total === 1) label = btn.dataset.labelShowOne || '';
      else label = (btn.dataset.labelShow || '').replace('{count}', String(total));
      setCountNode(labelEl, label);
      // Only touch the disabled attribute when the empty/non-empty state flips,
      // so routine filtering doesn't churn attributes the MutationObserver watches.
      const isEmpty = total === 0;
      if (isEmpty !== lastResultsEmpty) {
        btn.disabled = isEmpty;
        btn.setAttribute('aria-disabled', String(isEmpty));
        lastResultsEmpty = isEmpty;
      }
    }

    function applyFilters(options = {}) {
      const { preserveScroll = false, skipSort = false } = options;
      const scrollContainer = getScrollContainer();
      const scrollX = preserveScroll ? scrollContainer.scrollLeft : 0;
      const scrollY = preserveScroll ? getScrollTop() : 0;
      let visibleTotal = 0;
      const visibleIds = new Set();

      cards.forEach((card) => {
        const visible = cardIsVisible(card);
        card.hidden = !visible;
        if (visible) {
          visibleIds.add(card.dataset.productId || card.id);
        }
      });

      visibleTotal = visibleIds.size;

      sections.forEach((section) => {
        const sectionCards = section.querySelectorAll('.coffee-collection__item');
        const anyVisible = Array.from(sectionCards).some((card) => !card.hidden);
        section.hidden = !anyVisible;
      });

      updateResultsCount(visibleTotal);
      if (emptyEl) {
        emptyEl.hidden = visibleTotal > 0;
      }

      updateActiveFilterCount();
      renderActiveFilterChips();
      if (!skipSort) {
        sortSectionGrids({ preserveScroll });
      }
      updateFilterCounts();

      if (preserveScroll) {
        restoreScrollPosition(scrollX, scrollY);
      }
    }

    function updateActiveFilterCount() {
      let active = 0;
      if (state.subscription.length && state.subscription[0] !== 'all') active += 1;
      if (state.browse.length && state.browse[0] !== 'all') active += 1;
      if (state.availability.length && state.availability[0] !== DEFAULT_AVAILABILITY) active += 1;
      if (searchQuery.trim()) active += 1;
      active += state.sale.length + state.origin.length + state.process.length + state.flavour.length + state.descriptor.length + state.strength.length + state.roastStrength.length;

      root.querySelectorAll('[data-coffee-filter-count]').forEach((el) => {
        el.textContent = String(active);
        el.hidden = active === 0;
      });

      root.querySelectorAll('[data-coffee-clear]').forEach((btn) => {
        btn.hidden = active === 0;
      });
    }

    /** @type {Map<string, string> | null} */
    let descriptorLabels = null;

    function getDescriptorLabel(slug) {
      if (!descriptorLabels) {
        descriptorLabels = new Map();
        cards.forEach((card) => {
          parseEntries(card.dataset.descriptorEntries).forEach(({ slug: s, label }) => {
            if (!descriptorLabels.has(s)) descriptorLabels.set(s, label);
          });
        });
      }
      return descriptorLabels.get(slug) || slug;
    }

    /** @type {Map<string, { hue: number, sat: number, light: number }> | null} */
    let descriptorColors = null;
    let taxonomyFetchStarted = false;

    /** Family colours from the wheel taxonomy, so flavour chips match the wheel. */
    function ensureDescriptorColors() {
      if (descriptorColors || taxonomyFetchStarted) return;
      const src = document.querySelector('[data-coffee-wheel-modal]')?.dataset.wheelSrc;
      if (!src) return;
      taxonomyFetchStarted = true;
      fetch(src)
        .then((r) => r.json())
        .then((data) => {
          descriptorColors = new Map();
          (data.categories || []).forEach((cat) => {
            (cat.descriptors || []).forEach((d) => descriptorColors.set(d.slug, cat.color));
          });
          renderActiveFilterChips();
        })
        .catch(() => {});
    }

    function getFilterLabel(group, value) {
      if (group === 'roastStrength') return ROAST_STRENGTH_LABELS[value] || value;
      const input = root.querySelector(
        `[data-coffee-filter-input][data-filter-group="${group}"][data-filter-value="${CSS.escape(value)}"]`
      );
      const text = input
        ?.closest('label')
        ?.querySelector(
          '.coffee-filters__text, .coffee-filters__pill-text, .coffee-collection__subscription-text'
        );
      const raw = (text?.textContent || '').trim();
      if (raw) return raw;
      return value.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    }

    function makeFilterChip(container, label, onRemove, colors, iconHtml) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'coffee-collection__descriptor-chip';
      if (iconHtml) {
        const icon = document.createElement('span');
        icon.className = 'coffee-collection__descriptor-chip-icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML = iconHtml;
        chip.appendChild(icon);
      }
      if (colors) {
        chip.classList.add('coffee-collection__descriptor-chip--flavour');
        chip.style.setProperty('--chip-bg', `hsl(${colors.hue} ${colors.sat}% 90%)`);
        chip.style.setProperty('--chip-border', `hsl(${colors.hue} ${colors.sat}% 52%)`);
        chip.style.setProperty('--chip-text', `hsl(${colors.hue} ${Math.min(colors.sat + 8, 70)}% 26%)`);
      }
      const text = document.createElement('span');
      text.textContent = label;
      chip.appendChild(text);
      const cross = document.createElement('span');
      cross.className = 'coffee-collection__descriptor-chip-x';
      cross.setAttribute('aria-hidden', 'true');
      cross.textContent = '×';
      chip.appendChild(cross);
      chip.setAttribute('aria-label', `Remove filter ${label}`);
      chip.addEventListener('click', () => {
        onRemove();
        syncInputsFromState();
        applyFilters({ preserveScroll: true });
        writeStateToUrl();
      });
      container.appendChild(chip);
    }

    const CHIP_GROUP_LABELS = {
      descriptor: 'Flavours',
      browse: 'Browse',
      roastStrength: 'Roast',
      origin: 'Origin',
      process: 'Process',
      flavour: 'Profile',
      strength: 'Style',
      availability: 'Stock',
      subscription: 'Plan',
      sale: 'Sale',
      search: 'Search',
    };

    /** Active filters as labelled category clusters above the grid. */
    function renderActiveFilterChips() {
      const container = root.querySelector('[data-coffee-descriptor-chips]');
      if (!container) return;
      container.innerHTML = '';
      let count = 0;

      /** @type {Map<string, HTMLElement>} */
      const groups = new Map();
      const groupWrap = (key) => {
        if (!groups.has(key)) {
          const wrap = document.createElement('div');
          wrap.className = 'coffee-collection__chip-group';
          const label = document.createElement('span');
          label.className = 'coffee-collection__chip-group-label';
          label.textContent = CHIP_GROUP_LABELS[key] || key;
          wrap.appendChild(label);
          container.appendChild(wrap);
          groups.set(key, wrap);
        }
        return groups.get(key);
      };

      // wheel flavours first, coloured by family
      const slugs = state.descriptor.flatMap((token) => token.split('~')).filter(Boolean);
      if (slugs.length) ensureDescriptorColors();
      slugs.forEach((slug) => {
        makeFilterChip(
          groupWrap('descriptor'),
          getDescriptorLabel(slug),
          () => {
            state.descriptor = state.descriptor
              .map((token) => token.split('~').filter((s) => s !== slug).join('~'))
              .filter(Boolean);
          },
          descriptorColors?.get(slug)
        );
        count += 1;
      });
      if (slugs.length) {
        const edit = document.createElement('button');
        edit.type = 'button';
        edit.className = 'coffee-collection__descriptor-chip coffee-collection__descriptor-chip--edit';
        edit.textContent = container.dataset.labelEdit || 'Edit flavours';
        edit.addEventListener('click', () => {
          document.querySelector('[data-coffee-wheel-open]')?.click();
        });
        groupWrap('descriptor').appendChild(edit);
      }

      if (state.browse[0] && state.browse[0] !== 'all') {
        const value = state.browse[0];
        makeFilterChip(groupWrap('browse'), getFilterLabel('browse', value), () => {
          state.browse = [];
        });
        count += 1;
      }
      state.roastStrength.forEach((value) => {
        makeFilterChip(
          groupWrap('roastStrength'),
          getFilterLabel('roastStrength', value),
          () => {
            state.roastStrength = state.roastStrength.filter((v) => v !== value);
          },
          undefined,
          roastBeanHtml(value)
        );
        count += 1;
      });
      ['origin', 'process', 'flavour', 'strength'].forEach((group) => {
        state[group].forEach((value) => {
          makeFilterChip(groupWrap(group), getFilterLabel(group, value), () => {
            state[group] = state[group].filter((v) => v !== value);
          });
          count += 1;
        });
      });
      if (state.availability[0] && state.availability[0] !== DEFAULT_AVAILABILITY) {
        makeFilterChip(groupWrap('availability'), getFilterLabel('availability', state.availability[0]), () => {
          state.availability = [DEFAULT_AVAILABILITY];
        });
        count += 1;
      }
      if (state.subscription[0] && state.subscription[0] !== 'all') {
        makeFilterChip(groupWrap('subscription'), getFilterLabel('subscription', state.subscription[0]), () => {
          state.subscription = [];
        });
        count += 1;
      }
      state.sale.forEach(() => {
        makeFilterChip(groupWrap('sale'), getFilterLabel('sale', 'on-sale'), () => {
          state.sale = [];
        });
        count += 1;
      });
      const trimmedSearch = searchQuery.trim();
      if (trimmedSearch) {
        makeFilterChip(groupWrap('search'), `“${trimmedSearch}”`, () => {
          searchQuery = '';
          syncSearchInput();
        });
        count += 1;
      }

      if (count >= 2) {
        const clear = document.createElement('button');
        clear.type = 'button';
        clear.className = 'coffee-collection__descriptor-chip coffee-collection__descriptor-chip--clear';
        clear.textContent = container.dataset.labelClear || 'Clear all';
        clear.addEventListener('click', clearAllFilters);
        container.appendChild(clear);
      }

      container.hidden = count === 0;
    }

    document.addEventListener('coffee:wheel-apply', (event) => {
      const slugs = Array.isArray(event.detail?.descriptors) ? event.detail.descriptors : [];
      state.descriptor = [...new Set(slugs)];
      // the wheel's roast toggle drives the same browse filter as the sidebar:
      // espresso/filter selects it; "all" clears it ONLY if a brew style was
      // active (never clobbers decaf/blends/singles chosen elsewhere)
      const browse = event.detail?.browse;
      if (browse === 'espresso' || browse === 'filter') {
        state.browse = [browse];
      } else if (browse === 'all' && (state.browse[0] === 'espresso' || state.browse[0] === 'filter')) {
        state.browse = [];
      }
      syncInputsFromState();
      applyFilters();
      writeStateToUrl();
      afterFilterInteraction(null);
    });

    function setBrowse(value) {
      if (!value || value === 'all') {
        state.browse = [];
      } else if (BROWSE_KEYS.has(value)) {
        state.browse = [value];
      }
      syncInputsFromState();
      applyFilters();
      writeStateToUrl();
      afterFilterInteraction(null);
    }

    function clearAllFilters() {
      Object.keys(state).forEach((key) => {
        state[key] = [];
      });
      state.availability = [DEFAULT_AVAILABILITY];
      searchQuery = '';
      syncSearchInput();
      if (sortSelect) sortSelect.value = DEFAULT_SORT;
      syncInputsFromState();
      applyFilters();
      writeStateToUrl();
      afterFilterInteraction(null, { closeDrawerIfOpen: true });
    }

    function handleFilterChange(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.matches('[data-coffee-filter-input]')) return;

      const group = input.dataset.filterGroup;
      const value = input.dataset.filterValue;
      if (!group || !value) return;

      if (group === 'roastStrength' && input.closest('[data-coffee-roast-strength-sidebar]')) return;
      if (group === 'browse') return;

      if (input.type === 'radio') {
        if (value === 'all' && (group === 'browse' || group === 'subscription')) {
          state[group] = [];
        } else {
          state[group] = input.checked ? [value] : [];
        }
        if (group === 'browse') {
          normalizeBrowseState();
        }
      } else if (input.type === 'checkbox') {
        if (input.checked) {
          if (!state[group].includes(value)) state[group].push(value);
        } else {
          state[group] = state[group].filter((v) => v !== value);
        }
      }

      syncInputsFromState();
      applyFilters();
      writeStateToUrl();
      afterFilterInteraction(input);
    }

    function escapeHtml(text) {
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    function formatFilterLabel(label) {
      const entries = normalizeEntry('', label);
      const trimmed = entries.length === 1 ? entries[0].label : String(label || '').trim();
      if (!trimmed || trimmed.startsWith('[')) return '';

      if (/[A-Z]/.test(trimmed.slice(1))) {
        return trimmed;
      }

      return trimmed
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    }

    function toSlug(text) {
      return String(text)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    function normalizeEntry(slug, label) {
      if (!slug && !label) return [];
      const trimmedLabel = String(label || slug)
        .trim()
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
      const trimmedSlug = String(slug || '').trim();

      if (trimmedLabel.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmedLabel);
          if (Array.isArray(parsed)) {
            return parsed
              .map((item) => {
                const itemLabel = String(item).trim();
                if (!itemLabel) return null;
                return { slug: toSlug(itemLabel), label: itemLabel };
              })
              .filter(Boolean);
          }
        } catch {
          const inner = trimmedLabel
            .slice(1, -1)
            .replace(/"/g, '')
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
          if (inner.length) {
            return inner.map((itemLabel) => ({ slug: toSlug(itemLabel), label: itemLabel }));
          }
        }
      }

      if (!trimmedSlug && !trimmedLabel) return [];
      const resolvedLabel = trimmedLabel || trimmedSlug;
      const resolvedSlug = trimmedSlug || toSlug(resolvedLabel);
      if (!resolvedSlug || resolvedLabel === 'MetaobjectDrop' || resolvedSlug === 'metaobjectdrop') return [];
      return [{ slug: resolvedSlug, label: resolvedLabel }];
    }

    function repairCardFacetAttributes(cardList) {
      const facets = ['origin', 'process', 'flavour', 'strength', 'descriptor'];

      cardList.forEach((card) => {
        facets.forEach((facet) => {
          const entriesKey = `${facet}Entries`;
          const raw = card.dataset[entriesKey];
          if (!raw) return;

          const entries = parseEntries(raw);
          if (!entries.length) return;

          card.dataset[facet] = entries.map((entry) => entry.slug).join('|');
          card.dataset[entriesKey] = entries.map((entry) => `${entry.slug}:${entry.label}`).join('|');
        });
      });
    }

    function parseEntries(raw) {
      if (!raw) return [];
      return raw
        .split('|')
        .flatMap((part) => {
          const colonIndex = part.indexOf(':');
          if (colonIndex === -1) {
            const slug = part.trim();
            return normalizeEntry(slug, slug);
          }
          const slug = part.slice(0, colonIndex).trim();
          const label = part.slice(colonIndex + 1).trim();
          return normalizeEntry(slug, label);
        })
        .filter(Boolean);
    }

    /**
     * @param {HTMLElement[]} cardList
     * @param {string} entriesKey - dataset key, e.g. 'originEntries'
     */
    function collectEntryCounts(cardList, entriesKey) {
      /** @type {Map<string, { label: string, count: number }>} */
      const map = new Map();
      /** @type {Map<string, Set<string>>} */
      const seenBySlug = new Map();

      cardList.forEach((card) => {
        const productId = card.dataset.productId || card.id;
        const raw = card.dataset[entriesKey];
        parseEntries(raw).forEach(({ slug, label }) => {
          const entry =
            entriesKey === 'processEntries' ? resolveProcessFilterEntry(slug, label) : { slug, label };
          if (!entry?.slug) return;

          const { slug: resolvedSlug, label: resolvedLabel } = entry;
          if (!seenBySlug.has(resolvedSlug)) seenBySlug.set(resolvedSlug, new Set());
          const seenProducts = seenBySlug.get(resolvedSlug);
          if (seenProducts.has(productId)) return;
          seenProducts.add(productId);

          const existing = map.get(resolvedSlug);
          if (existing) {
            existing.count += 1;
          } else {
            map.set(resolvedSlug, { label: resolvedLabel, count: 1 });
          }
        });
      });

      return map;
    }

    const ROAST_BEAN_TONES = {
      '1': '#c9a875',
      '2': '#aa8c61',
      '3': '#8b704d',
      '4': '#6b533a',
      '5': '#4c3726',
      '6': '#2d1b12',
    };

    /** Mini tinted bean matching the sidebar scale glyph. */
    function roastBeanHtml(level) {
      const tone = ROAST_BEAN_TONES[level] || '#8b704d';
      return `<span class="ccc-roast-bean" aria-hidden="true"><svg viewBox="0 0 128 128" focusable="false" aria-hidden="true">
        <path fill="${tone}" d="M64 14C41.5 14 22 35.8 22 64s19.5 50 42 50 42-21.8 42-50S86.5 14 64 14Z"/>
        <path fill="none" stroke="rgba(0,0,0,.18)" stroke-width="6" d="M64 14C41.5 14 22 35.8 22 64s19.5 50 42 50 42-21.8 42-50S86.5 14 64 14Z"/>
        <ellipse fill="rgba(255,255,255,.26)" cx="46" cy="42" rx="11" ry="17" transform="rotate(-20 46 42)"/>
        <path fill="none" stroke="rgba(0,0,0,.22)" stroke-width="9" stroke-linecap="round" d="M55 29c13 9.5 16.5 22 9 34.5-6.5 11-5.5 22 5.5 35"/>
        <path fill="none" stroke="rgba(255,255,255,.55)" stroke-width="5" stroke-linecap="round" d="M53 28c13 9.5 16.5 22 9 34.5-6.5 11-5.5 22 5.5 35"/>
      </svg></span>`;
    }

    function collectRoastStrengthCounts(cardList) {
      /** @type {Map<string, { label: string, count: number }>} */
      const map = new Map();
      /** @type {Map<string, Set<string>>} */
      const seenByLevel = new Map();

      cardList.forEach((card) => {
        const level = card.dataset.roastStrength;
        if (!level) return;
        const productId = card.dataset.productId || card.id;
        if (!seenByLevel.has(level)) seenByLevel.set(level, new Set());
        const seenProducts = seenByLevel.get(level);
        if (seenProducts.has(productId)) return;
        seenProducts.add(productId);

        const label = card.dataset.roastStrengthLabel || ROAST_STRENGTH_LABELS[level] || level;
        const existing = map.get(level);
        if (existing) {
          existing.count += 1;
        } else {
          map.set(level, { label, count: 1 });
        }
      });

      return map;
    }

    function fillRoastStrengthLists(containers, map) {
      const sortedLevels = Array.from(map.keys()).sort((a, b) => Number(a) - Number(b));

      containers.forEach((container) => {
        if (!container) return;
        container.innerHTML = '';

        sortedLevels.forEach((level) => {
          const { label, count } = map.get(level);
          const safeLevel = escapeHtml(level);
          const safeLabel = escapeHtml(label);
          const isToolbar = container.closest('[data-coffee-roast-strength-toolbar]');
          const li = document.createElement('li');
          li.className = isToolbar
            ? 'coffee-collection__roast-strength-option'
            : 'coffee-filters__option';

          const pillClass = isToolbar
            ? 'coffee-collection__roast-strength-pill'
            : 'coffee-filters__label coffee-filters__label--roast-strength';

          li.innerHTML = `
            <label class="${pillClass}">
              <input
                type="checkbox"
                class="coffee-filters__input visually-hidden"
                data-coffee-filter-input
                data-filter-group="roastStrength"
                data-filter-value="${safeLevel}"
              >
              ${roastBeanHtml(level)}
              <span class="coffee-roast-strength-filter__label">${safeLabel}</span>
              <span class="coffee-filters__count" data-filter-count>${count}</span>
            </label>
          `;

          container.appendChild(li);
        });
      });

      root.querySelectorAll('[data-coffee-roast-strength-toolbar]').forEach((toolbar) => {
        toolbar.hidden = sortedLevels.length === 0;
      });
    }

    function updateRoastStrengthSidebarScale(map) {
      root.querySelectorAll('[data-coffee-roast-strength-sidebar]').forEach((scale) => {
        const group = scale.closest('.coffee-filters__group');
        const hasAny = map.size > 0;
        if (group) group.hidden = !hasAny;

        scale.querySelectorAll('[data-coffee-filter-input]').forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          const level = input.dataset.filterValue;
          const available = level && map.has(level);
          if (!available && input.checked) {
            state.roastStrength = state.roastStrength.filter((value) => value !== level);
          }
        });
      });
    }

    function collectFacetMaps() {
      const uniqueCards = getUniqueCardsByProduct(cards);
      return {
        origins: collectEntryCounts(uniqueCards, 'originEntries'),
        processes: collectEntryCounts(uniqueCards, 'processEntries'),
        flavours: collectEntryCounts(uniqueCards, 'flavourEntries'),
        strengths: collectEntryCounts(uniqueCards, 'strengthEntries'),
        roastStrengths: collectRoastStrengthCounts(uniqueCards),
      };
    }

    /**
     * @param {ParentNode | null} scope
     * @param {ReturnType<typeof collectFacetMaps>} maps
     */
    function fillFilterListsInScope(scope, maps) {
      if (!scope) return;

      fillDynamicList(scope.querySelectorAll('[data-coffee-origin-list]'), maps.origins);
      fillDynamicList(scope.querySelectorAll('[data-coffee-process-list]'), maps.processes);
      fillDynamicList(scope.querySelectorAll('[data-coffee-flavour-list]'), maps.flavours, { pill: true });
      fillDynamicList(scope.querySelectorAll('[data-coffee-strength-list]'), maps.strengths);
      fillRoastStrengthLists(scope.querySelectorAll('[data-coffee-roast-strength-list]'), maps.roastStrengths);
    }

    function buildDynamicFilterOptions() {
      facetMaps = facetMaps || collectFacetMaps();

      fillFilterListsInScope(root.querySelector('.coffee-collection__sidebar'), facetMaps);
      updateRoastStrengthSidebarScale(facetMaps.roastStrengths);
    }

    function ensureDrawerDynamicFilters() {
      if (drawerDynamicFiltersReady) return;

      facetMaps = facetMaps || collectFacetMaps();
      fillFilterListsInScope(document.getElementById('coffee-filters-drawer'), facetMaps);
      drawerDynamicFiltersReady = true;
      syncInputsFromState();
      updateFilterCounts();
    }

    /**
     * @param {NodeListOf<HTMLElement>|HTMLElement[]} containers
     * @param {Map<string, { label: string, count: number }>} map
     * @param {{ pill?: boolean }} [options]
     */
    function fillDynamicList(containers, map, options = {}) {
      const pill = options.pill === true;

      containers.forEach((container) => {
        if (!container) return;
        container.innerHTML = '';
        const sorted = Array.from(map.entries()).sort((a, b) => {
          if (container.dataset.filterGroup === 'process') {
            const aIdx = PROCESS_FILTER_ORDER.indexOf(a[0]);
            const bIdx = PROCESS_FILTER_ORDER.indexOf(b[0]);
            if (aIdx !== -1 || bIdx !== -1) {
              if (aIdx === -1) return 1;
              if (bIdx === -1) return -1;
              if (aIdx !== bIdx) return aIdx - bIdx;
            }
          }

          if (pill) {
            const countDiff = b[1].count - a[1].count;
            if (countDiff !== 0) return countDiff;
          }
          return a[1].label.localeCompare(b[1].label, undefined, { sensitivity: 'base' });
        });

        sorted.forEach(([slug, { label, count }]) => {
          const displayLabel = formatFilterLabel(label);
          const safeLabel = escapeHtml(displayLabel);
          const safeSlug = escapeHtml(slug);
          const li = document.createElement('li');
          li.className = 'coffee-filters__option';

          if (pill) {
            li.innerHTML = `
          <label class="coffee-filters__pill">
            <input
              type="checkbox"
              class="coffee-filters__input visually-hidden"
              data-coffee-filter-input
              data-filter-group="${container.dataset.filterGroup}"
              data-filter-value="${safeSlug}"
            >
            <span class="coffee-filters__pill-check" aria-hidden="true"></span>
            <span class="coffee-filters__pill-text">${safeLabel}</span>
            <span class="coffee-filters__count" data-filter-count>${count}</span>
          </label>
        `;
          } else {
            li.innerHTML = `
          <label class="coffee-filters__label">
            <input
              type="checkbox"
              class="coffee-filters__input"
              data-coffee-filter-input
              data-filter-group="${container.dataset.filterGroup}"
              data-filter-value="${safeSlug}"
            >
            <span class="coffee-filters__text">${safeLabel}</span>
            <span class="coffee-filters__count" data-filter-count>${count}</span>
          </label>
        `;
          }

          container.appendChild(li);
        });
      });
    }

    // Journey cards
    root.querySelectorAll('[data-coffee-journey]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const value = btn.dataset.coffeeJourney;
        if (state.browse[0] === value) {
          setBrowse('all');
        } else {
          setBrowse(value);
        }
      });
    });

    root.addEventListener('change', handleFilterChange);

    function bindRoastStrengthSidebarScale(scale) {
      if (scale.dataset.roastStrengthBound === 'true') return;
      scale.dataset.roastStrengthBound = 'true';

      scale.addEventListener(
        'pointerdown',
        (event) => {
          if (event.button !== 0) return;
          const label = event.target.closest('.coffee-filters__bean-scale-option');
          if (!label || !scale.contains(label)) return;
          event.preventDefault();
        },
        true
      );

      scale.addEventListener(
        'click',
        (event) => {
          const label = event.target.closest('.coffee-filters__bean-scale-option');
          if (!label || !scale.contains(label)) return;

          const input = label.querySelector('[data-coffee-filter-input]');
          if (!(input instanceof HTMLInputElement) || input.disabled) return;

          const value = input.dataset.filterValue || '';
          if (!value) return;

          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          const alreadySelected = state.roastStrength[0] === value;
          state.roastStrength = alreadySelected ? [] : [value];

          syncInputsFromState();
          const inDrawer = !!input.closest('#coffee-filters-drawer');
          applyFilters({ preserveScroll: !inDrawer });
          writeStateToUrl();
          if (inDrawer) afterFilterInteraction(input);
        },
        true
      );
    }

    root.querySelectorAll('[data-coffee-roast-strength-sidebar]').forEach(bindRoastStrengthSidebarScale);

    function bindBrowseFilterToggle(listEl) {
      if (listEl.dataset.browseFilterBound === 'true') return;
      listEl.dataset.browseFilterBound = 'true';

      listEl.addEventListener(
        'click',
        (event) => {
          const label = event.target.closest('.coffee-filters__label--browse');
          if (!label || !listEl.contains(label)) return;

          const input = label.querySelector('[data-coffee-filter-input][data-filter-group="browse"]');
          if (!(input instanceof HTMLInputElement) || input.disabled) return;

          const value = input.dataset.filterValue || '';
          if (!value) return;

          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          const current = state.browse[0] || 'all';
          const alreadySelected = current === value;

          if (alreadySelected && value !== 'all') {
            setBrowse('all');
          } else if (!alreadySelected) {
            setBrowse(value);
          }
        },
        true
      );
    }

    root.querySelectorAll('.coffee-filters__list--browse').forEach(bindBrowseFilterToggle);

    root.querySelectorAll('[data-coffee-clear]').forEach((btn) => {
      btn.addEventListener('click', clearAllFilters);
    });

    root.querySelector('[data-coffee-show-results]')?.addEventListener('click', () => {
      afterFilterInteraction(null, { closeDrawerIfOpen: true });
    });

    root.querySelectorAll('[data-coffee-search]').forEach((searchInput) => {
      if (!(searchInput instanceof HTMLInputElement)) return;

      searchInput.addEventListener('input', () => {
        searchQuery = searchInput.value;
        window.clearTimeout(searchDebounceId);
        searchDebounceId = window.setTimeout(() => {
          syncSearchInput();
          applyFilters({ preserveScroll: true });
          writeStateToUrl();
        }, 150);
      });

      searchInput.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (!searchInput.value) return;
        event.preventDefault();
        searchQuery = '';
        syncSearchInput();
        window.clearTimeout(searchDebounceId);
        applyFilters({ preserveScroll: true });
        writeStateToUrl();
      });
    });

    if (sortSelect) {
      sortSelect.addEventListener('change', () => {
        void (async () => {
          if (normalizeSortValue(sortSelect.value) === 'best-selling') {
            await ensureBestSellingRanks({ eager: true });
          }
          applyFilters({ preserveScroll: true });
          writeStateToUrl();
        })();
      });
    }

    const filterDrawer = document.getElementById('coffee-filters-drawer');
    filterDrawer?.addEventListener('theme-drawer:open', () => {
      ensureDrawerDynamicFilters();
    });

    async function finishInitialLayout() {
      try {
        neutraliseResultsHashOnLoad();
        captureInitialGridOrders();
        repairCardFacetAttributes(cards);
        readStateFromUrl();
        syncInputsFromState();

        const sortBy = normalizeSortValue(sortSelect?.value || DEFAULT_SORT);
        const needsBestSelling = sortBy === 'best-selling';
        const ranksReady = !needsBestSelling || hydrateBestSellingRanksFromCache();

        buildDynamicFilterOptions();
        syncInputsFromState();
        applyFilters({ preserveScroll: true, skipSort: needsBestSelling && !ranksReady });

        if (needsBestSelling && !ranksReady) {
          await loadBestSellingRanks();
          sortSectionGrids({ preserveScroll: true });
          updateFilterCounts();
        }

        // One-time load pass: fill the static-group counts (browse/sale/availability/
        // subscription) in BOTH the closed drawer and the sub-1200px hidden sidebar,
        // which the runtime visibility guards skip. Overwrites (never increments), so
        // re-running after the applyFilters/best-selling passes can't double-count.
        updateFilterCounts({ includeHidden: true });
      } finally {
        root.setAttribute('data-coffee-layout-ready', '');
        settleInitialPageScroll();
      }
    }

    void finishInitialLayout();

    window.addEventListener('popstate', () => {
      readStateFromUrl();
      syncInputsFromState();
      applyFilters({ preserveScroll: true });
    });
  }

  /**
   * The section renders up to 250 products (Shopify's per-page cap). When the
   * collection is bigger, Liquid sets data-coffee-pages; fetch the other pages
   * via the Section Rendering API and append each card to its merch section
   * grid, so filters, counts and sort see every coffee.
   * @param {HTMLElement} root
   */
  async function appendRemainingPages(root) {
    const pages = parseInt(root.dataset.coffeePages || '1', 10);
    const sectionId = root.dataset.coffeeSectionId;
    if (!sectionId || !(pages > 1)) return;

    const requests = [];
    for (let page = 2; page <= Math.min(pages, 20); page += 1) {
      const url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set('page', String(page));
      url.searchParams.set('section_id', sectionId);
      requests.push(
        fetch(url.toString(), { credentials: 'same-origin' })
          .then((response) => (response.ok ? response.text() : ''))
          .catch(() => '')
      );
    }

    const htmls = await Promise.all(requests);
    htmls.forEach((html) => {
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      doc.querySelectorAll('[data-coffee-section]').forEach((fetchedSection) => {
        const key = fetchedSection.getAttribute('data-coffee-section');
        const grid = root.querySelector(`[data-coffee-section="${key}"] .coffee-collection__grid`);
        const items = fetchedSection.querySelectorAll('.coffee-collection__item');
        if (!grid || items.length === 0) return;
        const fragment = document.createDocumentFragment();
        items.forEach((item) => fragment.appendChild(document.importNode(item, true)));
        grid.appendChild(fragment);
      });
    });
  }

  /**
   * @param {HTMLElement} root
   */
  function startCoffeeCollection(root) {
    if (!root || root.dataset.coffeeCollectionInit === 'true') return;
    if (!root.dataset.coffeePages) {
      initCoffeeCollection(root);
      return;
    }
    if (root.dataset.coffeePagesLoading === 'true') return;
    root.dataset.coffeePagesLoading = 'true';
    appendRemainingPages(root)
      .catch(() => {})
      .finally(() => {
        delete root.dataset.coffeePagesLoading;
        initCoffeeCollection(root);
      });
  }

  function boot() {
    document.querySelectorAll('[data-coffee-collection]').forEach(startCoffeeCollection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target?.querySelector?.('[data-coffee-collection]');
    if (root) startCoffeeCollection(root);
  });
